import React from 'react';
import { useNavigate } from 'react-router-dom';

import headerBg from '../../assets/header-bg.svg';
import logo from '../../assets/logo.svg';
import {
  EDIT_PERSONA_ARIA_LABEL,
  PERSONA_LABEL,
  PERSONA_PROFILE_ROUTE,
  SET_PERSONA_LABEL,
} from '../../constants/persona';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import UserMenu from '../UserMenu';

import { AzureDevOpsIcon } from './AzureDevOpsIcon';
import { BillyIcon } from './BillyIcon';
import './TopBar.css';

/**
 * External URL for Billy, the business-facing feasibility & cost chat UI.
 * Configure via the BILLY_APP_URL env var; falls back to a placeholder.
 */
const BILLY_URL =
  import.meta.env.BILLY_APP_URL ||
  'https://app-business-interface.azurewebsites.net/';

interface TopBarProps {
  title?: string;
}

export const TopBar: React.FC<TopBarProps> = ({ title = 'Archie' }) => {
  const { isAuthenticated } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const personaLabel = profile?.persona ?? SET_PERSONA_LABEL;
  const personaButtonLabel = `${PERSONA_LABEL}: ${personaLabel}`;

  const handleTitleClick = () => {
    navigate('/');
  };

  const handlePersonaClick = () => {
    navigate(PERSONA_PROFILE_ROUTE);
  };

  const showAzureDevOps = isAuthenticated && profile?.ado_enabled === true;
  const showBilly = isAuthenticated && profile?.billy_enabled === true;
  const showPersona = isAuthenticated && profile !== null;

  return (
    <header
      className="top-bar"
      style={{
        backgroundImage: `url(${headerBg})`,
      }}
    >
      {/* Left side - Shell Logo and Title */}
      <button
        onClick={handleTitleClick}
        className="top-bar-title-button"
        aria-label="Navigate to home"
        tabIndex={0}
      >
        <h1 className="top-bar-title">
          {/* Shell Logo */}
          <img src={logo} alt="Shell Logo" className="w-8 h-8" />
          {title}
        </h1>
      </button>

      {/* Right side - User Menu */}
      <div className="flex items-center gap-3">
        {showBilly && (
          <a
            href={BILLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="top-bar-billy-button"
            aria-label="Billy — open the business feasibility & cost chat (opens in a new tab)"
            title="Billy — feasibility & cost chat"
          >
            {/* Billy advisor mark. */}
            <BillyIcon size={26} />
            <span className="top-bar-billy-label">Billy</span>
          </a>
        )}
        {showAzureDevOps && (
          <button
            onClick={() => navigate('/azure-devops')}
            className="top-bar-ado-button"
            aria-label="Azure DevOps"
            title="Azure DevOps"
          >
            {/* Shell-red Azure DevOps mark for Codie. */}
            <AzureDevOpsIcon size={24} />
            <span className="top-bar-ado-label">Codie</span>
          </button>
        )}
        {showPersona && (
          <button
            onClick={handlePersonaClick}
            className="top-bar-lob-button"
            aria-label={EDIT_PERSONA_ARIA_LABEL}
            title={personaButtonLabel}
          >
            {personaButtonLabel}
          </button>
        )}
        {isAuthenticated && <UserMenu />}
      </div>
    </header>
  );
};
